import clsx from 'clsx'
import { AlertErrorCircle, CheckCircle } from '@/assets/icons'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import type { ExternalStorageProvider } from '@/constants/constants-external-storage'
import { PROVIDER_COMPACT_DISPLAY_NAME, PROVIDER_ICON_COMPONENTS } from '@/user_files/external-storage/ProviderMeta'
import { SyncManagedByPopover } from '@/user_files/external-storage/components/SyncManagedByPopover'

export interface DriveConnectivityChipProps {
  provider: ExternalStorageProvider
  status: 'connected' | 'disconnected'
  syncedFolderPath?: string | null
  /** Show the Figma disconnected label before hover at desktop widths, as on the DMS source view. */
  persistentLabel?: boolean
  isConnector: boolean
  connectorLabel: string | null
  onReconnect: () => void
  onOpenMatterSettings: () => void
  onOpenDisconnectedMatterSettings?: () => void
}

/** The two status presentations in Figma's final Matter Files sync chip. */
export function DriveConnectivityChip({
  provider,
  status,
  syncedFolderPath,
  persistentLabel = false,
  isConnector,
  connectorLabel,
  onReconnect,
  onOpenMatterSettings,
  onOpenDisconnectedMatterSettings,
}: DriveConnectivityChipProps) {
  const providerLabel = PROVIDER_COMPACT_DISPLAY_NAME[provider]
  const ProviderIcon = PROVIDER_ICON_COMPONENTS[provider]
  const isConnected = status === 'connected'
  const showManagedByPopover = !isConnected && !isConnector && !onOpenDisconnectedMatterSettings
  const tooltip = isConnected ? `Synced to ${syncedFolderPath || `${providerLabel} folder`}` : `${providerLabel} is Disconnected`

  const chipClassName = clsx(
    'group inline-flex h-7 w-fit shrink-0 items-center gap-1 rounded-full border px-[5px]',
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
    isConnected
      ? 'border-brand-200 bg-brand-50 text-brand-600 hover-capable:hover:border-brand-300 hover-capable:hover:bg-brand-100 focus-visible:border-brand-300 focus-visible:bg-brand-100'
      : 'border-red-200 bg-red-50 text-red-700 hover-capable:hover:bg-red-100 hover-capable:hover:pl-1.5 hover-capable:hover:pr-3 focus-visible:bg-red-100 focus-visible:pl-1.5 focus-visible:pr-3',
    !isConnected && persistentLabel && 'lg:pl-1.5 lg:pr-3'
  )

  const contents = (
    <>
      {isConnected ? <CheckCircle className="size-4 shrink-0" aria-hidden /> : <AlertErrorCircle className="size-4 shrink-0" aria-hidden />}
      <ProviderIcon className={clsx('size-4 shrink-0', isConnected && 'hidden hover-capable:group-hover:block group-focus-visible:block')} aria-hidden />
      {!isConnected && (
        <span
          className={clsx(
            'text-sm font-medium tracking-[-0.15px] text-brand-neutral-950',
            'hidden hover-capable:group-hover:block group-focus-visible:block',
            persistentLabel && 'lg:block'
          )}
        >
          {isConnector ? 'Reconnect' : 'Disconnected'}
        </span>
      )}
    </>
  )

  if (showManagedByPopover) {
    return (
      <SyncManagedByPopover connectorLabel={connectorLabel} action="reconnect">
        <button type="button" className={chipClassName} aria-label={`${providerLabel} disconnected details`}>
          {contents}
        </button>
      </SyncManagedByPopover>
    )
  }

  return (
    <CustomizableTooltip
      describeChild
      disableInteractive
      placement="top-end"
      offset={[0, 3]}
      tooltipClassName="!m-0 !rounded-[2px] !bg-brand-neutral-800 !px-1 !py-0.5 !font-sans !text-[10.4px] !font-medium !leading-[10.4px] !tracking-[0.1248px]"
      title={tooltip}
    >
      <button
        type="button"
        className={chipClassName}
        aria-label={isConnected || !isConnector ? `Open matter settings for ${providerLabel} sync` : `Reconnect ${providerLabel}`}
        onClick={isConnected ? onOpenMatterSettings : isConnector ? onReconnect : onOpenDisconnectedMatterSettings}
      >
        {contents}
      </button>
    </CustomizableTooltip>
  )
}
